import React from 'react';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import type { LinkProps } from 'src/components/ui/Link';
import Link from 'src/components/ui/Link';
import HorizontalScroller, { SnapItem } from 'src/components/ui/HorizontalScroller';
import Typography from 'src/components/ui/Typography';
import MediaCarousel from 'src/components/ui/MediaCarousel';
import PostCard from 'src/components/news/posts/PostCard';
import { cardHeightInRemForPosts } from 'src/constants/misc';

import { colours } from 'src/constants/colours';
import theme from 'src/utils/theme';

import { usePostsQuery } from 'src/hooks/__generated__/queries';
import type { CompanyProfileQuery, Medium, Post } from 'src/types/__generated__/graphql';
import { routePathCompany } from 'src/constants/router';
import { styledComponent, styledTag } from 'src/utils/styled';

const StyledCardContent = styledComponent(CardContent)`
    position: relative;
    padding: 1.5rem 0;
`;

const MediaCarouselWrapper = styledTag('div')`
    height: 14rem;
    cursor: pointer;
`;

const StyledLink = styledComponent((props: LinkProps) => <Link {...props} />)`
    color: ${theme.palette.primary.main};
    font-size: 0.875rem;
    font-weight: bold;
`;

const cardsWidthInRem = 25;

const StyledSnapItem = styledComponent(SnapItem)`
    width: ${cardsWidthInRem}rem;
`;

export interface Props {
    companyProfile: NonNullable<CompanyProfileQuery['companyProfile']>;
}

const defaultLimit = 5;

const CompanyPostsSection: React.FC<Props> = ({ companyProfile }) => {
    const { t } = useTranslation(['profile']);

    const {
        previousData,
        data = previousData,
        fetchMore,
        loading,
    } = usePostsQuery({
        variables: {
            skip: 0,
            take: defaultLimit,
            companyId: String(companyProfile.id),
        },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    return (
        <Card elevation={0}>
            <StyledCardContent>
                <Box my={2}>
                    <Typography variant="h2" serif>
                        {t('profile:publications')}
                    </Typography>
                </Box>
                <HorizontalScroller
                    actualCardsLength={data?.posts?.length}
                    totalCardsLength={companyProfile.numberOfPosts}
                    loading={loading}
                    cardHeightInRem={cardHeightInRemForPosts}
                    cardWidthInPx={cardsWidthInRem * 16}
                    marginBottom={2}
                    handleFetchMore={() =>
                        fetchMore({
                            variables: {
                                skip: data?.posts?.length,
                            },
                        })
                    }
                >
                    {data?.posts?.map((post) => {
                        const images = () => (
                            <MediaCarouselWrapper>
                                <MediaCarousel media={post.media as Medium[]} alt={String(post.message)} />
                            </MediaCarouselWrapper>
                        );
                        return (
                            <StyledSnapItem key={post.id}>
                                <PostCard
                                    carousel={images}
                                    post={post as Post}
                                    showHeader={false}
                                    backgroundColor={colours.black.O04}
                                />
                            </StyledSnapItem>
                        );
                    })}
                </HorizontalScroller>
                {data?.posts?.length && data.posts.length > 0 ? (
                    <Box textAlign="right">
                        <StyledLink href={`${routePathCompany}/${companyProfile.id}/posts`}>
                            {t('profile:seeAllPublications')}
                        </StyledLink>
                    </Box>
                ) : null}
            </StyledCardContent>
        </Card>
    );
};

export default CompanyPostsSection;
